import { ExclamationCircleOutlined } from '@ant-design/icons';
import { PlanInfo } from '@mm/state/helpers/billing-info-helpers';
import { SubscriptionTabDefinition } from '@mm/state/helpers/billing-subscription-tab-helpers';
import { EnhancedBillingPage } from '@mm/state/interfaces/connected-page';
import { isBilling2023Subscription } from '@mm/state/model/SubscriptionModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { companyAdminSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import React from 'react';
import { useSelector } from 'react-redux';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { useFreeTrialUpgradeModal } from '../../../../../../../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import { getIsApproachingCreditsLimit, getOverageText } from '../../helpers';
import OveragesManagement from '../../OveragesManagement/OveragesManagement';
import '../BillingPageCard.pcss';
import PromoRedemptionSelector from '../PromoRedemptionSelector/PromoRedemptionSelector';
import { useBillingDetails } from '../use-billing-details';

type Props = {
  page: EnhancedBillingPage;
  planInfo: Partial<PlanInfo>;
  planTab: SubscriptionTabDefinition;
};

export const CreditsLimitReachedWarning = (props: Props) => {
  const { page, planInfo, planTab } = props;
  const { teamSubscription } = planTab;
  const { canManageOverages, shouldShowCredits } = useBillingDetails(
    page,
    planInfo,
    teamSubscription,
  );
  const promotions = useSelector(
    (state: StoreState) => state.promotions.entities,
  );
  const isCompanyAdmin = useSelector(companyAdminSelector);
  const showPromotionsSelector =
    !!planInfo.isFreePlan && !!promotions.length && isCompanyAdmin;

  const { openFreeTrialUpgradeModal } = useModalActions();
  const {
    isFreeTrialPromotionActive,
    isFreeTrialPromotionWithoutSubscription,
  } = useFreeTrialUpgradeModal();

  const approachingLimits = getIsApproachingCreditsLimit(
    page.subscription || teamSubscription,
    planTab.tabDetails.teamMessageStatistics,
    page.messagesStatistic.utilizationPercentage,
  );
  const isNewBillingSubscription =
    page.subscription && isBilling2023Subscription(page.subscription);

  const overageText = getOverageText(
    planInfo,
    isFreeTrialPromotionActive,
    approachingLimits,
    isNewBillingSubscription,
  );

  if (
    !page.outgoingMessageUsage?.pageLockedAt ||
    (!isNewBillingSubscription && !!page.messagesStatistic.messagesRemaining) ||
    page.overagesEnabled ||
    !shouldShowCredits
  ) {
    return <></>;
  }

  return (
    <div
      className={classNames('billing-card__credits-limit-reached-wrapper', {
        'billing-card__credits-aproaching-limit': approachingLimits,
      })}
    >
      <ExclamationCircleOutlined
        className={classNames(
          'billing-card__credits-limit-reached-exclamation-icon',
          {
            'billing-card__credits-limit-approaching-exclamation-icon':
              approachingLimits,
          },
        )}
      />

      <div className="billing-card__overage-title">
        {isNewBillingSubscription
          ? 'Your account is locked'
          : approachingLimits
          ? 'Approaching Credits Limit'
          : 'Credits Limit Reached'}
        <div className="billing-card__overage-text">{overageText}</div>
      </div>

      <div className="billing-card__overage-wrapper">
        {(canManageOverages || teamSubscription) && (
          <OveragesManagement
            facebookPage={page}
            className="billing-card__enable-overages-button"
            hideQuestionIcon={isNewBillingSubscription}
            textOverride={
              isNewBillingSubscription
                ? 'Enable overage payments  '
                : 'Enable automatic upgrades'
            }
          />
        )}

        {isFreeTrialPromotionWithoutSubscription && (
          <PrimaryButton
            className="billing-card-right-section__upgrade-button billing-card-right-section__overage-upgrade-button"
            color={ButtonColor.blue}
            onClick={() => openFreeTrialUpgradeModal(page)}
          >
            Upgrade
          </PrimaryButton>
        )}

        {showPromotionsSelector && (
          <PromoRedemptionSelector
            facebookPageId={page.id}
            promotions={promotions}
          />
        )}
      </div>
    </div>
  );
};
